import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { Preferences } from '@capacitor/preferences'
import { fetchConfig } from '@/services/api/config'
import { STORAGE_KEYS } from '@/utils/constants'
import type { ConfigurationError } from '@/types/geofence'
import { BleClient } from '@capacitor-community/bluetooth-le'
import { Capacitor } from '@capacitor/core'

/**
 * Time range with from/to boundaries
 */
export interface TimeRange {
    from: string // e.g., "07:00:00" or "07:00"
    to: string // e.g., "11:00:59" or "11:00"
}

/**
 * Interval schedule: check-in must be within start range, check-out within end range
 */
export interface IntervalWorkSchedule {
    type: 'interval'
    duration: string // e.g., "08:00:00"
    start: TimeRange
    end: TimeRange
    daysOfWeek: number[]
}

/**
 * Strict schedule: must check-in before "from" time, check-out after "to" time
 */
export interface StrictWorkSchedule {
    type: 'strict'
    duration: string
    from: string // e.g., "08:30:59" - must check in before this
    to: string // e.g., "17:00:00" - must check out after this
    daysOfWeek: number[]
}

/**
 * Flexible schedule: check-in within start range, check-out within end range,
 * with offset tolerances
 */
export interface FlexibleWorkSchedule {
    type: 'flexible'
    duration: string
    max_offset_start: string // e.g., "01:00:00"
    min_offset_end: string // e.g., "00:30:00"
    start: TimeRange
    end: TimeRange
    daysOfWeek: number[]
}

export type WorkSchedule = IntervalWorkSchedule | StrictWorkSchedule | FlexibleWorkSchedule

export interface GeofenceConfig {
    points: Array<{ lat: number; lng: number }>
    center: { lat: number; lng: number }
    radiusMeters: number
}

export interface AppConfig {
    workSchedule: WorkSchedule
    geofence: GeofenceConfig
    lateThresholdMinutes: number
    holidays: string[]
}

const CACHE_KEY = STORAGE_KEYS.APP_CONFIG
const CACHE_EXPIRY_HOURS = 2
const BLE_ATTENDANCE_KEY = 'ble_attendance_enabled'

export const useSettingsStore = defineStore('settings', () => {
    const config = ref<AppConfig | null>(null)
    const isLoaded = ref(false)
    const isLoading = ref(false)
    const lastFetchTime = ref<string | null>(null)
    const error = ref<string | null>(null)
    const configurationError = ref<ConfigurationError | null>(null)

    // BLE attendance settings
    const bleAttendanceEnabled = ref(false)
    const blePermissionsGranted = ref(false)

    // Computed getters for config properties
    const workSchedule = computed(() => config.value?.workSchedule ?? null)
    const geofence = computed(() => config.value?.geofence ?? null)
    const lateThresholdMinutes = computed(() => config.value?.lateThresholdMinutes ?? 15)
    const holidays = computed(() => config.value?.holidays ?? [])
    const workStartTime = computed(() => {
        const ws = config.value?.workSchedule
        if (!ws) return '09:00'
        switch (ws.type) {
            case 'interval': return ws.start.to // late if after the end of start window
            case 'strict': return ws.from
            case 'flexible': return ws.start.to
        }
    })
    const workEndTime = computed(() => {
        const ws = config.value?.workSchedule
        if (!ws) return '17:00'
        switch (ws.type) {
            case 'interval': return ws.end.from // early if before the start of end window
            case 'strict': return ws.to
            case 'flexible': return ws.end.from
        }
    })

    /**
     * Load cached config from local storage
     */
    async function loadCachedConfig(): Promise<void> {
        try {
            const { value } = await Preferences.get({ key: CACHE_KEY })
            if (value) {
                const cached = JSON.parse(value)
                config.value = cached.config
                lastFetchTime.value = cached.fetchTime
                isLoaded.value = true
            }
        } catch (err) {
            const error = createCacheMissingError()
            configurationError.value = error
            console.error('[SettingsStore] Failed to load cached config:', err)
        }
    }

    /**
     * Check if cache has expired (> 24 hours old)
     */
    function isCacheExpired(): boolean {
        if (!lastFetchTime.value) return true

        const hoursSinceLastFetch =
            (Date.now() - new Date(lastFetchTime.value).getTime()) / (1000 * 60 * 60)

        return hoursSinceLastFetch >= CACHE_EXPIRY_HOURS
    }

    /**
     * Save config to cache
     */
    async function saveToCache(appConfig: AppConfig): Promise<void> {
        try {
            const fetchTime = new Date().toISOString()
            await Preferences.set({
                key: CACHE_KEY,
                value: JSON.stringify({
                    config: appConfig,
                    fetchTime
                })
            })
            lastFetchTime.value = fetchTime
        } catch (err) {
            console.error('[SettingsStore] Failed to save config to cache', err)
        }
    }

    /**
     * Load config from API and cache it
     * Falls back to cached config if API call fails
     * Skips API call if user is not authenticated
     */
    async function loadConfig(): Promise<void> {
        // Import userStore dynamically to avoid circular dependency
        const { useUserStore } = await import('@/stores/user')
        const userStore = useUserStore()

        // Skip API call if user is not authenticated
        // Just use cache if available
        if (!userStore.isAuthenticated) {
            console.log('[SettingsStore] User not authenticated, skipping config fetch')
            // Cache will be loaded on next authenticated session
            return
        }

        // Skip if cache is fresh
        if (!isCacheExpired() && config.value) {
            return
        }

        isLoading.value = true
        error.value = null
        configurationError.value = null

        try {
            const apiConfig = await fetchConfig()
            config.value = apiConfig
            isLoaded.value = true
            await saveToCache(apiConfig)
        } catch (err) {
            console.error('[SettingsStore] Failed to fetch config from API:', err)

            // Determine error type and create appropriate error
            let configError: ConfigurationError
            if (err && typeof err === 'object' && 'message' in err) {
                const errorMessage = (err as { message: string }).message.toLowerCase()
                if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
                    configError = createNetworkError()
                } else if (errorMessage.includes('parse') || errorMessage.includes('json')) {
                    configError = createParseError()
                } else {
                    configError = createApiFailureError()
                }
            } else {
                configError = createApiFailureError()
            }

            configurationError.value = configError

            // Fallback to cache if available
            if (config.value) {
                error.value = 'Using cached configuration (offline)'
            } else {
                error.value = configError.userMessage
                console.error('[SettingsStore] No cached config available')
                // Don't throw error - just log it
                // App can still function without config for unauthenticated users
            }
        } finally {
            isLoading.value = false
        }
    }

    /**
     * Force refresh config from API (clears cache expiry check)
     */
    async function forceRefresh(): Promise<void> {
        lastFetchTime.value = null
        await loadConfig()
    }

    /**
     * Clear config cache
     */
    async function clearCache(): Promise<void> {
        try {
            await Preferences.remove({ key: CACHE_KEY })
            lastFetchTime.value = null
        } catch (err) {
            console.error('[SettingsStore] Failed to clear cache', err)
        }
    }

    function setConfig(appConfig: AppConfig) {
        config.value = appConfig
        isLoaded.value = true
    }

    function updateWorkSchedule(schedule: WorkSchedule) {
        if (config.value) {
            config.value.workSchedule = schedule
        }
    }

    function updateGeofence(geofence: GeofenceConfig) {
        if (config.value) {
            config.value.geofence = geofence
        }
    }

    function reset() {
        config.value = null
        isLoaded.value = false
        lastFetchTime.value = null
        error.value = null
        configurationError.value = null
    }

    /**
     * Load BLE attendance setting from storage
     */
    async function loadBleAttendanceSetting(): Promise<void> {
        try {
            const { value } = await Preferences.get({ key: BLE_ATTENDANCE_KEY })
            bleAttendanceEnabled.value = value === 'true'
            console.log('[SettingsStore] BLE attendance enabled:', bleAttendanceEnabled.value)
        } catch (err) {
            console.error('[SettingsStore] Failed to load BLE attendance setting:', err)
            bleAttendanceEnabled.value = false
        }
    }

    /**
     * Update BLE attendance setting
     */
    async function setBleAttendanceEnabled(enabled: boolean): Promise<void> {
        try {
            await Preferences.set({
                key: BLE_ATTENDANCE_KEY,
                value: enabled.toString()
            })
            bleAttendanceEnabled.value = enabled
            console.log('[SettingsStore] BLE attendance setting updated:', enabled)
        } catch (err) {
            console.error('[SettingsStore] Failed to save BLE attendance setting:', err)
            throw err
        }
    }

    /**
     * Request BLE permissions (this will trigger the permission dialog)
     * Should be called when user explicitly wants to enable BLE attendance
     */
    async function requestBlePermissions(): Promise<boolean> {
        const platform = Capacitor.getPlatform()

        // BLE is only available on native platforms
        if (platform === 'web') {
            blePermissionsGranted.value = false
            return false
        }

        try {
            // Initialize BLE Client - this will request permissions if not granted
            await BleClient.initialize()
            blePermissionsGranted.value = true
            console.log('[SettingsStore] BLE permissions granted')
            return true
        } catch (error) {
            console.error('[SettingsStore] BLE permissions denied:', error)
            blePermissionsGranted.value = false
            return false
        }
    }

    /**
     * Check if BLE permissions are already granted (without requesting)
     */
    async function checkBlePermissions(): Promise<boolean> {
        const platform = Capacitor.getPlatform()

        // BLE is only available on native platforms
        if (platform === 'web') {
            blePermissionsGranted.value = false
            return false
        }

        try {
            // Try to initialize BLE Client to check if permissions are already granted
            // This won't trigger a permission dialog if already granted
            await BleClient.initialize()
            blePermissionsGranted.value = true
            return true
        } catch (error) {
            // Permissions not granted yet or denied
            console.log('[SettingsStore] BLE permissions not yet granted')
            blePermissionsGranted.value = false
            return false
        }
    }

    /**
     * Create API failure error
     */
    function createApiFailureError(): ConfigurationError {
        return {
            error: 'API_FAILURE',
            message: 'Failed to fetch configuration from API',
            userMessage: 'Configuration error. Please check your connection.',
            action: 'Check your internet connection and try again',
            severity: 'error'
        }
    }

    /**
     * Create network error
     */
    function createNetworkError(): ConfigurationError {
        return {
            error: 'NETWORK_ERROR',
            message: 'Network error while fetching configuration',
            userMessage: 'Network error. Please check your connection.',
            action: 'Check your internet connection and try again',
            severity: 'error'
        }
    }

    /**
     * Create parse error
     */
    function createParseError(): ConfigurationError {
        return {
            error: 'PARSE_ERROR',
            message: 'Failed to parse configuration data',
            userMessage: 'Configuration error. Contact IT support.',
            action: 'Contact your IT administrator to fix the configuration',
            severity: 'error'
        }
    }

    /**
     * Create cache missing error
     */
    function createCacheMissingError(): ConfigurationError {
        return {
            error: 'CACHE_MISSING',
            message: 'Failed to load cached configuration',
            userMessage: 'Configuration error. Please check your connection.',
            action: 'Check your internet connection and try again',
            severity: 'warning'
        }
    }

    return {
        // State
        config,
        isLoaded,
        isLoading,
        lastFetchTime,
        error,
        configurationError,
        bleAttendanceEnabled,
        blePermissionsGranted,
        // Computed getters
        workSchedule,
        geofence,
        lateThresholdMinutes,
        holidays,
        workStartTime,
        workEndTime,
        // Methods
        loadCachedConfig,
        loadConfig,
        forceRefresh,
        clearCache,
        setConfig,
        updateWorkSchedule,
        updateGeofence,
        reset,
        loadBleAttendanceSetting,
        setBleAttendanceEnabled,
        checkBlePermissions,
        requestBlePermissions
    }
})
